import { Controller } from '@hotwired/stimulus'

// Inline price / stock on workspace products index - saves on Enter (or blur if changed).
// Variant products: expand the Variants panel and edit each size/colour stock the same way.
export default class extends Controller {
  static targets = ['price', 'stock', 'status', 'lowStock', 'variantsPanel', 'variantsToggle', 'variantStock']
  static values = {
    url: String,
    price: String,
    stock: Number,
    open: { type: Boolean, default: false }
  }

  connect () {
    this.saving = false
    this.variantSnapshots = new Map()
    this.snapshot()
    this.syncVariantsPanel()
  }

  snapshot () {
    if (this.hasPriceTarget) this.priceValue = this.priceTarget.value
    if (this.hasStockTarget && !this.stockTarget.disabled) {
      this.stockValue = Number(this.stockTarget.value)
    }
    this.variantStockTargets.forEach((input) => {
      this.variantSnapshots.set(input.dataset.variantId, input.value)
    })
  }

  toggleVariants (event) {
    if (event) event.preventDefault()
    this.openValue = !this.openValue
    this.syncVariantsPanel()
  }

  openValueChanged () {
    this.syncVariantsPanel()
  }

  syncVariantsPanel () {
    if (!this.hasVariantsPanelTarget) return

    this.variantsPanelTarget.classList.toggle('hidden', !this.openValue)
    if (this.hasVariantsToggleTarget) {
      this.variantsToggleTarget.setAttribute('aria-expanded', this.openValue ? 'true' : 'false')
      const label = this.variantsToggleTarget.querySelector('[data-label]')
      if (label) label.textContent = this.openValue ? 'Hide' : 'Variants'
      this.variantsToggleTarget.querySelector('svg')?.classList.toggle('rotate-180', this.openValue)
    }
  }

  onPriceKeydown (event) {
    if (event.key !== 'Enter') return
    event.preventDefault()
    this.save({ sale_price: this.priceTarget.value })
  }

  onStockKeydown (event) {
    if (event.key !== 'Enter') return
    event.preventDefault()
    this.save({ stock_quantity: this.stockTarget.value })
  }

  onVariantStockKeydown (event) {
    if (event.key !== 'Enter') return
    event.preventDefault()
    const input = event.currentTarget
    this.save({
      variant_id: input.dataset.variantId,
      stock_quantity: input.value
    }, input)
  }

  onPriceBlur () {
    if (!this.hasPriceTarget) return
    if (this.priceTarget.value === this.priceValue) return
    this.save({ sale_price: this.priceTarget.value })
  }

  onStockBlur () {
    if (!this.hasStockTarget || this.stockTarget.disabled) return
    if (Number(this.stockTarget.value) === this.stockValue) return
    this.save({ stock_quantity: this.stockTarget.value })
  }

  onVariantStockBlur (event) {
    const input = event.currentTarget
    const id = input.dataset.variantId
    if (input.value === this.variantSnapshots.get(id)) return
    this.save({
      variant_id: id,
      stock_quantity: input.value
    }, input)
  }

  async save (payload, activeInput = null) {
    if (this.saving || !this.urlValue) return

    this.saving = true
    this.setStatus('Saving…', 'pending')

    try {
      const body = new URLSearchParams()
      Object.entries(payload).forEach(([key, value]) => body.append(key, value ?? ''))

      const response = await fetch(this.urlValue, {
        method: 'PATCH',
        headers: {
          'X-CSRF-Token': this.csrfToken,
          Accept: 'application/json',
          'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8'
        },
        credentials: 'same-origin',
        body: body.toString()
      })

      const data = await response.json().catch(() => ({}))

      if (!response.ok || !data.ok) {
        const message = (data.errors && data.errors[0]) || 'Could not save.'
        this.setStatus(message, 'error')
        this.revert(payload, activeInput)
        return
      }

      if (Object.prototype.hasOwnProperty.call(data, 'sale_price') && this.hasPriceTarget) {
        this.priceTarget.value = data.sale_price
        this.priceValue = data.sale_price
      }

      if (Object.prototype.hasOwnProperty.call(data, 'stock_quantity') && this.hasStockTarget) {
        this.stockTarget.value = String(data.stock_quantity)
        if (!this.stockTarget.disabled) this.stockValue = Number(data.stock_quantity)
      }

      if (data.variant_id != null) {
        const input = activeInput || this.variantStockTargets.find((el) => el.dataset.variantId === String(data.variant_id))
        if (input) {
          input.value = String(data.variant_stock)
          this.variantSnapshots.set(String(data.variant_id), String(data.variant_stock))
          this.flashElement(input)
        }
      }

      if (this.hasLowStockTarget) {
        this.lowStockTarget.classList.toggle('hidden', !data.low_stock)
      }

      if (!payload.variant_id) this.flashField(payload)
      this.setStatus('Saved', 'ok')
    } catch (_error) {
      this.setStatus('Network error - try again.', 'error')
      this.revert(payload, activeInput)
    } finally {
      this.saving = false
    }
  }

  revert (payload, activeInput = null) {
    if (Object.prototype.hasOwnProperty.call(payload, 'sale_price') && this.hasPriceTarget) {
      this.priceTarget.value = this.priceValue
    }
    if (Object.prototype.hasOwnProperty.call(payload, 'stock_quantity') && !payload.variant_id && this.hasStockTarget && !this.stockTarget.disabled) {
      this.stockTarget.value = String(this.stockValue)
    }
    if (payload.variant_id) {
      const input = activeInput || this.variantStockTargets.find((el) => el.dataset.variantId === String(payload.variant_id))
      if (input) input.value = this.variantSnapshots.get(String(payload.variant_id)) ?? input.value
    }
  }

  flashField (payload) {
    const targets = []
    if (Object.prototype.hasOwnProperty.call(payload, 'sale_price') && this.hasPriceTarget) targets.push(this.priceTarget)
    if (Object.prototype.hasOwnProperty.call(payload, 'stock_quantity') && this.hasStockTarget) targets.push(this.stockTarget)
    targets.forEach((el) => this.flashElement(el))
  }

  flashElement (el) {
    if (!el) return
    el.classList.add('ring-2', 'ring-emerald-400', 'border-emerald-400')
    window.setTimeout(() => {
      el.classList.remove('ring-2', 'ring-emerald-400', 'border-emerald-400')
    }, 900)
  }

  setStatus (message, tone) {
    if (!this.hasStatusTarget) return

    const tones = {
      pending: 'text-slate-500',
      ok: 'text-emerald-700',
      error: 'text-rose-700'
    }
    const className = `min-h-[1rem] text-[10px] font-semibold ${tones[tone] || tones.pending}`

    this.statusTargets.forEach((el) => {
      el.textContent = message
      el.className = className
    })

    if (tone === 'ok') {
      window.clearTimeout(this._statusTimer)
      this._statusTimer = window.setTimeout(() => {
        this.statusTargets.forEach((el) => { el.textContent = '' })
      }, 1600)
    }
  }

  get csrfToken () {
    return document.querySelector('meta[name="csrf-token"]')?.content
  }
}
